/**
 * useUpdateCategory Hook
 *
 * Updates an existing category using URQL mutation.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import { extractGraphQLErrorMessage } from "@/lib/graphql-utils";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const UpdateCategoryDocument = `
  mutation UpdateCategory($id: ID!, $input: CategoryInput!) {
    updateCategory(id: $id, input: $input) { id }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

export interface CategoryUpdateInput {
  name: string;
  description: string | null;
  color: string | null;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
}

interface UseUpdateCategoryResult {
  updateCategory: (id: string, input: CategoryUpdateInput) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Update an existing category.
 */
export function useUpdateCategory(): UseUpdateCategoryResult {
  const [result, executeMutation] = useMutation<
    { updateCategory: { id: string } },
    { id: string; input: CategoryUpdateInput }
  >(UpdateCategoryDocument);

  const updateCategory = useCallback(
    async (id: string, input: CategoryUpdateInput): Promise<void> => {
      try {
        const response = await executeMutation({ id, input });

        if (response.error) {
          const message = extractGraphQLErrorMessage(response.error, "Failed to update category");
          throw new Error(message);
        }

        errorTracking.addBreadcrumb({
          category: "admin",
          message: "Category updated",
          data: { categoryId: id },
        });
      } catch (err) {
        const updateError =
          err instanceof Error ? err : new Error("Failed to update category");

        errorTracking.captureError(updateError, {
          tags: { feature: "admin", action: "updateCategory" },
        });

        throw updateError;
      }
    },
    [executeMutation]
  );

  return {
    updateCategory,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
